import { Injectable } from '@angular/core';
import { BusinessStrategyService } from 'src/app/providers/input/business-strategy.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface FilterState {
  [key: string]: any;
}

export interface FilterField {
  key: string;
  label: string;
  type: 'dropdown' | 'text' | 'date' | 'multi-select';
  options?: any[];
  isCustomField?: boolean;
  isOrphans?: boolean;
  disabled?: boolean;
}

export interface FilterConfig {
  title: string;
  fields: FilterField[];
  data: any[];
  customFields?: any[];
  fieldOptions?: { [key: string]: any[] };
}

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  private mappingDataCache: any = null;
  private mappingDataLoading: boolean = false;

  constructor(private businessStrategyService: BusinessStrategyService) { }

  /**
   * Fetch mapping data for orphans filter
   * This is called only when needed (when orphans filter is active)
   * @param entityType Type of entity: 'projects', 'applications', 'suppliers', or 'otdata'
   */
  fetchMappingData(entityType: 'projects' | 'applications' | 'suppliers' | 'otdata'): Observable<any> {
    // Return cached data if available
    if (this.mappingDataCache) {
      return of(this.mappingDataCache[entityType] || {});
    }

    // Return empty object if already loading to prevent multiple simultaneous calls
    if (this.mappingDataLoading) {
      return of({});
    }

    this.mappingDataLoading = true;

    // For OT data, we need tech themes too, so always fetch them
    return this.businessStrategyService.getMappingData(entityType === 'projects' || entityType === 'otdata').pipe(
      map((mappingData) => {
        this.mappingDataCache = mappingData;
        this.mappingDataLoading = false;
        return mappingData[entityType] || {};
      }),
      catchError((error) => {
        console.error('Error fetching mapping data:', error);
        this.mappingDataLoading = false;
        return of({});
      })
    );
  }

  /**
   * Clear cached mapping data
   */
  clearMappingDataCache(): void {
    this.mappingDataCache = null;
  }

  /**
   * Apply filters to data array
   * @param data Original data array
   * @param filters Applied filters
   * @param customFields Custom fields configuration
   * @param fieldConfig Field configuration to determine field types
   * @param entityType Type of entity for orphans filter: 'projects', 'applications', 'suppliers', or 'otdata'
   * @returns Observable of filtered data array
   */
  applyFiltersAsync(data: any[] = [], filters: FilterState = {}, customFields: any[] = [], fieldConfig: FilterField[] = [], entityType: 'projects' | 'applications' | 'suppliers' | 'otdata' = 'projects'): Observable<any[]> {
    if (!filters || Object.keys(filters).length === 0) {
      return of(data);
    }

    // Check if orphans filter is active
    const hasOrphansFilter = filters['orphans'] && 
                            filters['orphans'] !== '' && 
                            filters['orphans'] !== 'Please Select' &&
                            !(Array.isArray(filters['orphans']) && filters['orphans'].length === 0);

    // If orphans filter is active, fetch mapping data first
    if (hasOrphansFilter) {
      return this.fetchMappingData(entityType).pipe(
        map(mappingData => this.applyFilters(data, filters, customFields, fieldConfig, mappingData, entityType))
      );
    }

    // Otherwise, apply filters without mapping data
    return of(this.applyFilters(data, filters, customFields, fieldConfig, {}, entityType));
  }

  /**
   * Apply filters to data array (synchronous version)
   * @param data Original data array
   * @param filters Applied filters
   * @param customFields Custom fields configuration
   * @param fieldConfig Field configuration to determine field types
   * @param mappingData Mapping data(Business Objectives and Tech Themes) for orphans filter
   * @param entityType Type of entity for search fields: 'projects', 'applications', 'suppliers', or 'otdata'
   * @returns Filtered data array
   */
  applyFilters(data: any[] = [], filters: FilterState = {}, customFields: any[] = [], fieldConfig: FilterField[] = [], mappingData: any = {}, entityType: 'projects' | 'applications' | 'suppliers' | 'otdata' = 'projects'): any[] {
    if (!filters || Object.keys(filters).length === 0) {
      return data;
    }

    return data.filter(item => {
      return Object.keys(filters).every(filterKey => {
        const filterValue = filters[filterKey];
        
        // Skip empty filters
        if (!filterValue || filterValue === '' || filterValue === 'Please Select' || 
            (Array.isArray(filterValue) && filterValue.length === 0)) {
          return true;
        }

        // Skip disabled fields
        const field = fieldConfig?.find(f => f.key === filterKey);
        if (field?.disabled) {
          return true;
        }

        // Handle orphans filter
        if (filterKey === 'orphans') {
          return this.checkOrphansFilter(item, filterValue, mappingData);
        }

        // Handle custom fields
        if (filterKey.startsWith('custom_')) {
          return this.checkCustomFieldFilter(item, filterKey, filterValue, customFields);
        }

        // Handle search field (searches across name and description fields)
        if (filterKey === 'search') {
          return this.checkSearchFilter(item, filterValue, entityType);
        }

        // Handle benefits filter (searches across multiple benefit fields)
        if (filterKey === 'benefits') {
          return this.checkBenefitsFilter(item, filterValue);
        }

        // Handle regular fields
        return this.checkRegularFieldFilter(item, filterKey, filterValue, fieldConfig);
      });
    });
  }

  /**
   * Check if item matches search filter (searches across name and description fields)
   */
  private checkSearchFilter(item: any, filterValue: any, entityType: 'projects' | 'applications' | 'suppliers' | 'otdata' = 'projects'): boolean {
    if (!filterValue || filterValue.toString().trim() === '') {
      return true;
    }

    const searchTerm = filterValue.toString().toLowerCase().trim();
    
    // Define the fields to search in based on the data type
    const searchFields = this.getSearchFields(entityType);
    
    // Search across all specified fields
    return searchFields.some(fieldKey => {
      const fieldValue = this.getNestedValue(item, fieldKey);
      if (!fieldValue) return false;
      
      const fieldValueStr = fieldValue.toString().toLowerCase();
      
      // Use contains matching for better search experience
      return fieldValueStr.includes(searchTerm);
    });
  }

  /**
   * Get search fields based on entity type
   */
  private getSearchFields(entityType: 'projects' | 'applications' | 'suppliers' | 'otdata' = 'projects'): string[] {
    switch (entityType) {
      case 'projects':
        return ['name', 'desc', 'prgName'];
      case 'applications':
        return ['name', 'desc', 'appName'];
      case 'suppliers':
        return ['name', 'desc', 'supplierName'];
      case 'otdata':
        return ['assetName', 'assetDescription'];
      default:
        return ['name', 'desc', 'prgName', 'appName', 'supplierName', 'empName'];
    }
  }

  /**
   * Check if item matches benefits filter (searches across multiple benefit fields)
   */
  private checkBenefitsFilter(item: any, filterValue: any): boolean {
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
      return true;
    }

    const selectedBenefits = Array.isArray(filterValue) ? filterValue : [filterValue];
    
    // Check if any of the selected benefits match any of the benefit fields
    return selectedBenefits.some((benefit: string) => {
      const benefitStr = benefit.toString().toLowerCase().trim();
      
      // First check the benefits array (most likely structure)
      if (item.benefits && Array.isArray(item.benefits)) {
        const hasMatchingBenefit = item.benefits.some((benefitObj: any) => {
          if (benefitObj.benefit) {
            const itemBenefitStr = benefitObj.benefit.toString().toLowerCase().trim();
            return itemBenefitStr === benefitStr || itemBenefitStr.includes(benefitStr);
          }
          return false;
        });
        if (hasMatchingBenefit) return true;
      }
      
      // Also check benefits_realisations array if it exists
      if (item.benefits_realisations && Array.isArray(item.benefits_realisations)) {
        const hasMatchingBenefit = item.benefits_realisations.some((benefitObj: any) => {
          if (benefitObj.benefit) {
            const itemBenefitStr = benefitObj.benefit.toString().toLowerCase().trim();
            return itemBenefitStr === benefitStr || itemBenefitStr.includes(benefitStr);
          }
          return false;
        });
        if (hasMatchingBenefit) return true;
      }
      
      // If no match found in any structure, return false
      return false;
    });
  }

  /**
   * Check if item matches regular field filter
   */
  private checkRegularFieldFilter(item: any, fieldKey: string, filterValue: any, fieldConfig?: FilterField[]): boolean {
    const itemValue = this.getNestedValue(item, fieldKey);
    
    if (!itemValue) {
      return false;
    }

    // Find field configuration to determine field type
    const field = fieldConfig?.find(f => f.key === fieldKey);
    
    if (field?.type === 'text') {
      // For text fields, use contains matching
      const itemValueStr = itemValue.toString().toLowerCase().trim();
      const filterValueStr = filterValue.toString().toLowerCase().trim();
      return itemValueStr.includes(filterValueStr);
    } else if (Array.isArray(filterValue)) {
      // For multi-select fields, check if item value is in the selected array
      const itemValueStr = itemValue.toString().toLowerCase().trim();
      return filterValue.some((val: any) => val.toString().toLowerCase().trim() === itemValueStr);
    } else {
      // For single-select dropdown fields, use exact matching
      const itemValueStr = itemValue.toString().toLowerCase().trim();
      const filterValueStr = filterValue.toString().toLowerCase().trim();
      return itemValueStr === filterValueStr;
    }
  }

  /**
   * Check if item matches custom field filter
   */
  private checkCustomFieldFilter(item: any, fieldKey: string, filterValue: any, customFields?: any[]): boolean {
    if (!customFields) {
      return false;
    }

    const customFieldId = fieldKey.replace('custom_', '');
    const customField = customFields.find(cf => cf._id === customFieldId);
    
    if (!customField) {
      return false;
    }

    // Check multiple possible data structures for custom fields
    let customFieldData = null;
    
    if (item.categories && Array.isArray(item.categories)) {
      customFieldData = item.categories.find((cat: any) => cat._id === customFieldId);
    } else if (item.prgCategory && Array.isArray(item.prgCategory)) {
      customFieldData = item.prgCategory.find((cat: any) => cat._id === customFieldId);
    }
    
    if (!customFieldData || !customFieldData.value) {
      return false;
    }

    const itemValueStr = customFieldData.value.toString().toLowerCase().trim();
    
    if (Array.isArray(filterValue)) {
      // For multi-select fields, check if item value is in the selected array
      return filterValue.some((val: any) => val.toString().toLowerCase().trim() === itemValueStr);
    } else {
      // For single-select fields, use exact matching
      const filterValueStr = filterValue.toString().toLowerCase().trim();
      return itemValueStr === filterValueStr;
    }
  }

  /**
   * Check if item matches orphans filter
   */
  private checkOrphansFilter(item: any, filterValue: string | string[], mappingData?: any): boolean {
    // Handle both string and array values (for multi-select compatibility)
    const values = Array.isArray(filterValue) ? filterValue : [filterValue];
    
    // If no values selected, show all items
    if (values.length === 0 || values.includes('') || values.includes('Please Select')) {
      return true;
    }
    
    const isOrphanResult = this.isOrphan(item, mappingData);
    
    // Check if any of the selected values match the item's orphan status
    if (values.includes('yes') && isOrphanResult) {
      return true; // Show orphan items when 'yes' is selected
    }
    if (values.includes('no') && !isOrphanResult) {
      return true; // Show non-orphan items when 'no' is selected
    }
    
    return false;
  }

  /**
   * Check if item is an orphan (not mapped to any Business Objective or Tech Theme)
   */
  private isOrphan(item: any, mappingData?: any): boolean {
    // First check if we have mapping data from API
    if (mappingData && item._id) {
      const projectMapping = mappingData[item._id];
      
      if (projectMapping) {
        // If project has any business objectives or tech themes mapping, it's not an orphan
        const hasBusinessObjectives = projectMapping.businessObjectives && projectMapping.businessObjectives.length > 0;
        const hasTechThemes = projectMapping.techThemes && projectMapping.techThemes.length > 0;
        
        if (hasBusinessObjectives || hasTechThemes) return false;
        // If project exists in mapping data but has no mappings, it's an orphan
        return true;
      }
    }

    return true;
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Get unique values for a field from data array
   */
  getUniqueValues(data: any[], fieldKey: string): any[] {
    const values = new Set();
    
    data.forEach(item => {
      const value = this.getNestedValue(item, fieldKey);
      if (value && value.toString().trim() !== '') {
        values.add(value.toString().trim());
      }
    });

    return Array.from(values).sort();
  }

  /**
   * Get unique values for custom field from data array
   */
  getUniqueValuesForCustomField(data: any[], customFieldId: string): any[] {
    const values = new Set();
    
    data.forEach(item => {
      // Check multiple possible data structures for custom fields
      let customFieldData = null;
      
      if (item.categories && Array.isArray(item.categories)) {
        customFieldData = item.categories.find((cat: any) => cat._id === customFieldId);
      } else if (item.prgCategory && Array.isArray(item.prgCategory)) {
        customFieldData = item.prgCategory.find((cat: any) => cat._id === customFieldId);
      }
      
      if (customFieldData && customFieldData.value && customFieldData.value.toString().trim() !== '') {
        values.add(customFieldData.value.toString().trim());
      }
    });

    return Array.from(values).sort();
  }

  /**
   * Clear all filters
   */
  clearFilters(): FilterState {
    return {};
  }

  /**
   * Check if any filters are applied
   */
  hasActiveFilters(filters: FilterState): boolean {
    if (!filters) return false;
    
    return Object.values(filters).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value && value !== '' && value !== 'Please Select';
    });
  }

  /**
   * Get count of active filters
   */
  getActiveFilterCount(filters: FilterState): number {
    if (!filters) return 0;
    
    return Object.values(filters).filter(value => 
      value && value !== '' && value !== 'Please Select'
    ).length;
  }
}
